import React from 'react';
import { formatDateTime, getStatusConfig } from '../../lib/utils';
import { cn } from '../../lib/utils';
import {
  FileText,
  Send,
  CheckCircle,
  XCircle,
  DollarSign,
  Clock,
  Edit,
} from 'lucide-react';

const getActionIcon = (action) => {
  const icons = {
    CREATED: FileText,
    UPDATED: Edit,
    SUBMITTED: Send,
    MANAGER_APPROVED: CheckCircle,
    MANAGER_REJECTED: XCircle,
    ACCOUNTS_APPROVED: CheckCircle,
    ACCOUNTS_REJECTED: XCircle,
    PAID: DollarSign,
  };
  return icons[action] || Clock;
};

const getActionColor = (action) => {
  const colors = {
    CREATED: 'bg-gray-100 text-gray-600',
    UPDATED: 'bg-blue-100 text-blue-600',
    SUBMITTED: 'bg-blue-100 text-blue-600',
    MANAGER_APPROVED: 'bg-green-100 text-green-600',
    MANAGER_REJECTED: 'bg-red-100 text-red-600',
    ACCOUNTS_APPROVED: 'bg-emerald-100 text-emerald-600',
    ACCOUNTS_REJECTED: 'bg-orange-100 text-orange-600',
    PAID: 'bg-green-100 text-green-700',
  };
  return colors[action] || 'bg-gray-100 text-gray-600';
};

export const AuditTimeline = ({ logs }) => {
  if (!logs || logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No audit history available
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="audit-timeline">
      {logs.map((log, index) => {
        const Icon = getActionIcon(log.action);
        const isLast = index === logs.length - 1;
        
        return (
          <div key={log.id} className="relative flex gap-4" data-testid={`audit-log-${log.id}`}>
            {/* Timeline Line */}
            {!isLast && (
              <div className="absolute left-5 top-10 bottom-0 w-px bg-gray-200" />
            )}
            
            {/* Icon */}
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10",
                getActionColor(log.action)
              )}
            >
              <Icon size={18} />
            </div>
            
            {/* Content */}
            <div className="flex-1 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="font-medium text-sm text-gray-900">
                  {log.action.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-muted-foreground">
                  by {log.performedByName}
                </span>
              </div>
              
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatDateTime(log.performedAt)}
              </p>
              
              {log.note && (
                <p className="text-sm text-gray-600 mt-2 p-3 bg-gray-50 rounded-md border border-gray-100">
                  "{log.note}"
                </p>
              )}
              
              {log.fromStatus && log.toStatus && (
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <span className={cn(
                    "px-2 py-0.5 rounded border",
                    getStatusConfig(log.fromStatus).color
                  )}>
                    {getStatusConfig(log.fromStatus).label}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className={cn(
                    "px-2 py-0.5 rounded border",
                    getStatusConfig(log.toStatus).color
                  )}>
                    {getStatusConfig(log.toStatus).label}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AuditTimeline;
